"use client";

import {
  Content as ContentPrimitive,
  DialogDescription,
  DialogTitle,
  Portal as PortalPrimitive,
} from "@radix-ui/react-dialog";
import { Presence } from "@radix-ui/react-presence";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { mergeRefs, useObjectRef } from "@react-aria/utils";
import { type ComponentProps, useCallback, useMemo } from "react";

import { cn } from "../../utils";
import { FloatingPortalContainerContext } from "../portal/FloatingPortalContainerContext";
import { isToastInteraction } from "../toast/is-toast-interaction";
import { useDialogRootContext } from "./context";
import Overlay from "./Overlay";
import { useDialogNestedContext, useDialogStackContext } from "./stack/context";
import StackPane from "./stack/StackPane";

export type DialogContentProps = ComponentProps<typeof ContentPrimitive> & {
  overlayClassName?: string;
  /**
   * Width classes the modal box adopts while this view is on top of a `Dialog.Stack`.
   * Ignored outside a stack, where `className` sizes the box directly.
   */
  width?: string;
};

const Content = ({
  ref: forwardedRef,
  className,
  children,
  overlayClassName,
  width,
  onPointerDownOutside,
  ...props
}: DialogContentProps) => {
  const { contentRef } = useDialogRootContext();
  const stack = useDialogStackContext();
  const nested = useDialogNestedContext();
  const ref = useObjectRef(
    useMemo(() => (contentRef ? mergeRefs(forwardedRef, contentRef) : forwardedRef), [contentRef, forwardedRef])
  );

  const setOpen = nested?.setOpen;
  const requestClose = useCallback(() => setOpen?.(false), [setOpen]);

  if (stack && nested) {
    return (
      <Presence present={nested.open}>
        <StackPane
          ref={ref}
          paneKey={nested.paneKey}
          present={nested.open}
          requestClose={requestClose}
          width={width}
          className={className}
        >
          {children}
        </StackPane>
      </Presence>
    );
  }

  return (
    <FloatingPortalContainerContext.Provider value={null}>
      <PortalPrimitive>
        <Overlay className={cn("p-4", overlayClassName)}>
          <VisuallyHidden>
            <DialogTitle />
            <DialogDescription />
          </VisuallyHidden>
          <ContentPrimitive
            ref={ref}
            className={cn(
              "relative top-[13vh] left-[50%] z-50 flex max-h-[74vh] w-full max-w-3xl translate-x-[-50%] flex-col gap-6 overflow-hidden rounded-xl bg-white py-6 shadow-xl",
              className
            )}
            onPointerDownOutside={event => {
              if (isToastInteraction(event)) {
                event.preventDefault();
                return;
              }
              onPointerDownOutside?.(event);
            }}
            {...props}
          >
            {children}
          </ContentPrimitive>
        </Overlay>
      </PortalPrimitive>
    </FloatingPortalContainerContext.Provider>
  );
};

Content.displayName = ContentPrimitive.displayName;

export default Content;
